import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { useFormContext } from 'react-hook-form';
import { first } from 'lodash';
import { DropdownChangeEvent, DropdownProps } from 'primereact/dropdown';
import { FormEvent } from 'primereact/ts-helpers';
import { NO_BREAK_SPACE } from '../../../../utils/constants';
import { RecomendacaoSemeaduraFormType } from '../types';
import { FormAdubacao } from './form-controller';
import useDropdownFilter from '../../../../hooks/use-dropdown-filter';
import { ItensSaldoService, UnidadesService } from '../../../../services';
import {
  AdubacaoRow,
  AdubacaoTalhao,
  ItemSaldo,
  UnidadeConversao,
} from '../../../../@types';
import { convertAdubos } from '../../utils';
import Agro1Loader from '../../../../components/Agro1Loader';
import Agro1Toast from '../../../../components/Agro1Toast';
import ItemSaldoOptionTemplate from '../../../../components/ItemSaldoOptionTemplate';

const Form: FC<PropTypes> = ({
  formObject,
  isEditing,
  onCancel,
  onSubmit,
  recomendacaoSemeadura,
}) => {
  const [talhoes, adubosList, propriedade] = formObject.form.watch([
    'talhoes',
    'adubos',
    'propriedade',
  ]);
  const formAdubos = useFormContext<AdubacaoRow>();
  const {
    setValue,
    watch,
    formState: { isValid },
  } = formAdubos;
  const [talhao, adubo] = watch(['talhao', 'adubo']);
  const [unidades, setUnidades] = useState<UnidadeConversao[]>([]);
  const [adubosOptions, setAdubosOptions] = useState<ItemSaldo[]>([]);
  const [loadingAdubos, setLoadingAdubos] = useState(false);
  const { filterQuery: aduboFilterQuery, onFilter: onFilterAdubo } =
    useDropdownFilter();

  useEffect(() => {
    if (!propriedade) return;
    let cancelled = false;
    setLoadingAdubos(true);
    setAdubosOptions([]);
    ItensSaldoService.saldos({
      propriedadeId: propriedade.id,
      operacaoItem: 'adubacao',
      descricao: aduboFilterQuery,
    })
      .then((data) => {
        if (!cancelled) setAdubosOptions(data);
      })
      .finally(() => {
        if (!cancelled) setLoadingAdubos(false);
      });
    return () => {
      cancelled = true;
    };
  }, [propriedade, aduboFilterQuery]);

  const talhoesList = useMemo<AdubacaoTalhao[]>(
    () =>
      talhoes.map(({ talhao: gleba, identification, area }) => ({
        id: identification,
        talhao: gleba,
        area,
      })),
    [talhoes],
  );

  const getTalhaoOptionDescricao = useCallback(
    (option: AdubacaoTalhao) => {
      const area = talhoes.find(
        ({ identification }) => identification === option.id,
      )?.area;

      return `${option.talhao!.descricao} (${area}) ha`;
    },
    [talhoes],
  );

  const talhaoOptionTemplate = useCallback(
    (option: AdubacaoTalhao) => {
      if (!option?.talhao) return NO_BREAK_SPACE;

      return getTalhaoOptionDescricao(option);
    },
    [getTalhaoOptionDescricao],
  );

  const talhaoValueTemplate = useCallback(
    (option: AdubacaoTalhao, props: DropdownProps) => {
      if (!option?.talhao) return props.placeholder;

      return getTalhaoOptionDescricao(option);
    },
    [getTalhaoOptionDescricao],
  );

  const fetchUnidades = useCallback(async (unidadeId: number) => {
    const loaderId = Agro1Loader.show();
    let newUnidades;

    try {
      newUnidades = await UnidadesService.listConversao(unidadeId);

      setUnidades(newUnidades || []);
    } catch (error) {
      console.error(error);
      Agro1Toast.show({
        detail: 'Falha ao carregar unidades.',
        severity: 'error',
        sticky: true,
      });
    } finally {
      Agro1Loader.close(loaderId);
    }

    return newUnidades;
  }, []);

  const onTalhaoChange = (event: DropdownChangeEvent) =>
    setValue('area', event.value?.area || null);

  useEffect(() => {
    if (!isEditing) return;

    if (!adubo) {
      setUnidades([]);
      return;
    }

    fetchUnidades(+adubo.unidade.id);
  }, [adubo, isEditing, fetchUnidades]);

  useEffect(() => {
    if (!recomendacaoSemeadura || !adubosOptions?.length) return;

    const loadAdubos = async () => {
      const adubosToLoad = await convertAdubos(
        recomendacaoSemeadura,
        adubosOptions,
      );

      formObject.form.setValue('adubos', adubosToLoad);
    };

    loadAdubos();
  }, [recomendacaoSemeadura, formObject.form, adubosOptions]);

  const autoFillDose = useCallback(
    (newAduboId: number) => {
      if (!talhao) return;

      const lastLine = adubosList.findLast(
        (row) => row.adubo?.id === newAduboId,
      );

      setValue('dose', lastLine?.dose || null);
    },
    [adubosList, talhao, setValue],
  );

  const onChangeAdubo = async (event: FormEvent<ItemSaldo>) => {
    if (!event.value) return;

    autoFillDose(event.value.id);

    const newUnidades = await fetchUnidades(+event.value.unidade.id);

    if (!newUnidades?.length) return;

    const selectedUnidade = first(newUnidades);
    setValue('unidade', selectedUnidade!);
  };

  return (
    <div className="col-12">
      <div className="formgrid grid mt-5">
        <FormAdubacao.Dropdown
          options={talhoesList}
          dataKey="id"
          optionLabel="descricao"
          label="Talhão:"
          name="talhao"
          containerClassName="col-6 md:col-4 bold-font-size"
          placeholder="Todos os talhões"
          showClear={!isEditing}
          onChange={onTalhaoChange}
          itemTemplate={talhaoOptionTemplate}
          valueTemplate={talhaoValueTemplate}
          dependencies={['adubo']}
        />
        {talhao && (
          <FormAdubacao.InputNumber
            label="Área:"
            name="area"
            containerClassName="col-6 md:col-4"
            className="w-full"
            addonRight="ha"
          />
        )}
        <FormAdubacao.Dropdown
          options={adubosOptions}
          dataKey="id"
          optionLabel="descricao"
          label="Adubo:"
          name="adubo"
          containerClassName="col-6 md:col-4"
          itemTemplate={ItemSaldoOptionTemplate}
          onChange={onChangeAdubo}
          filter
          loading={loadingAdubos}
          dropdownIcon={loadingAdubos ? 'pi pi-spin pi-spinner' : undefined}
          onFilter={onFilterAdubo}
        />
        <FormAdubacao.Dropdown
          options={unidades}
          dataKey="id"
          optionLabel="abreviatura"
          label="Unidade:"
          name="unidade"
          containerClassName="col-6 md:col-4"
        />
        <FormAdubacao.InputNumber
          label="Dose:"
          name="dose"
          containerClassName="col-6 md:col-4"
          className="w-full"
        />
      </div>
      <div className="flex justify-content-end">
        <div className="flex flex-row">
          {isEditing && (
            <Button
              type="button"
              className="button-danger mr-1"
              label="Cancelar"
              onClick={onCancel}
            />
          )}
          <Button
            type="button"
            className="button-secondary"
            label="Salvar"
            disabled={!isValid}
            onClick={onSubmit}
          />
        </div>
      </div>
    </div>
  );
};

type PropTypes = RecomendacaoSemeaduraFormType & {
  isEditing: boolean;
  onCancel: () => void;
  onSubmit: () => void;
};

export default Form;
